import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { Box, Stack, Typography, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import { DataAccessProvider } from '@constants/constants';
import appsflyerLogo from '../../assets/icons/appsflyer-logo.png';
import adjustLogo from '../../assets/icons/adjust-logo.png';
import googleDriveLogo from '../../assets/icons/google-drive-logo.png';
import snowFlakeLogo from '../../assets/icons/snowflake-logo.png';
import bigQueryLogo from '../../assets/icons/big-query-logo.png';
import gcsLogo from '../../assets/icons/gcs-logo.png';
import s3Logo from '../../assets/icons/s3-logo.png';
import mySqlLogo from '../../assets/icons/mysql-logo.png';
import aggregatedCohortDataLogo from '../../assets/icons/aggregated-cohort-data-icon.png';
import { useTranslation } from 'react-i18next';
import { isNullOrUndefined } from '@utils/common';

export interface StatusIndicator {
  text: string;
  color: string;
}

interface CustomAccordionProps {
  title: string;
  defaultOpen?: boolean;
  isSuccess?: boolean;
  subTitle?: string;
  children: ReactNode;
  open?: boolean;
  showEdit?: boolean;
  onEdit?: () => void;
  disableExpand?: boolean;
  provider?: DataAccessProvider;
  showProvider?: boolean;
  borderStyle?: string;
  boxShadow?: string;
  hideExpand?: boolean;
  onToggleAccordion?: (open: boolean) => void;
  renderButton?: () => ReactNode;
  statusIndicator?: StatusIndicator | null;
}

const CustomAccordion: FC<CustomAccordionProps> = ({
  children,
  title,
  defaultOpen,
  isSuccess,
  subTitle,
  open,
  showEdit,
  onEdit,
  disableExpand = false,
  provider,
  showProvider = false,
  borderStyle,
  boxShadow,
  hideExpand = false,
  onToggleAccordion,
  renderButton,
  statusIndicator,
}) => {
  const [isExpanded, setIsExpanded] = useState(!!defaultOpen);
  const { t: translation } = useTranslation();

  const getProviderDetails = useCallback(() => {
    switch (provider) {
      case DataAccessProvider.APPS_FLYER:
        return { iconUrl: appsflyerLogo, label: translation('gettingStarted.providers.appsflyer') };
      case DataAccessProvider.ADJUST:
        return { iconUrl: adjustLogo, label: translation('gettingStarted.providers.adjust') };
      case DataAccessProvider.BIGQUERY:
        return { iconUrl: bigQueryLogo, label: translation('gettingStarted.providers.bigquery') };
      case DataAccessProvider.GCS:
        return {
          iconUrl: gcsLogo,
          label: translation('gettingStarted.providers.google_cloud_storage'),
        };
      case DataAccessProvider.S3:
        return { iconUrl: s3Logo, label: translation('gettingStarted.providers.amazon_s3') };
      case DataAccessProvider.MYSQL:
        return { iconUrl: mySqlLogo, label: translation('gettingStarted.providers.my_sql') };
      case DataAccessProvider.SNOWFLAKE:
        return { iconUrl: snowFlakeLogo, label: translation('gettingStarted.providers.snowflake') };
      case DataAccessProvider.GOOGLE_DRIVE:
        return {
          iconUrl: googleDriveLogo,
          label: translation('gettingStarted.providers.direct_file_share'),
        };
      case DataAccessProvider.AGGREGATED_DATA:
        return {
          iconUrl: aggregatedCohortDataLogo,
          label: translation('gettingStarted.providers.aggregated_cohort_data'),
        };
      case DataAccessProvider.NONE:
        return { iconUrl: undefined, label: translation('gettingStarted.providers.other') };
      default:
        return { iconUrl: undefined, label: translation('gettingStarted.providers.other') };
    }
  }, [provider, translation]);

  useEffect(() => {
    if (open !== undefined) {
      setIsExpanded(!!open);
    }
  }, [open]);

  const handleToggleAccordion = () => {
    if (disableExpand) return;
    const newOpen = !isExpanded;
    setIsExpanded(newOpen);
    onToggleAccordion?.(newOpen);
  };

  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        borderRadius: '1rem',
        border: borderStyle,
        boxShadow: boxShadow,
        padding: '1.5rem 1.25rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        transition: 'all 0.3s ease-in-out',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: 1 }}>
          <Box>
            <Typography fontSize="1.15rem" lineHeight="1.375rem" fontWeight={700}>
              {title}
            </Typography>
            {subTitle && (
              <Typography fontSize="0.875rem" lineHeight="1.25rem" fontWeight={400} color="#162936">
                {subTitle}
              </Typography>
            )}
          </Box>

          {showProvider && (
            <Box
              sx={{
                background: 'transparent',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                justifyContent: 'center',
                color: '#162936',
                fontSize: '0.875rem',
                fontWeight: 700,
                lineHeight: '1.25rem',
                letterSpacing: '-0.35px',
              }}
            >
              {getProviderDetails().iconUrl && (
                <img
                  src={getProviderDetails().iconUrl || ''}
                  alt={`logo-${provider}`}
                  style={{ height: '100%' }}
                />
              )}
              {getProviderDetails().label}
            </Box>
          )}

          {isSuccess && (
            <CheckCircleIcon
              sx={{
                color: '#2F736E',
                fontSize: '1.25rem',
              }}
            />
          )}

          {!isSuccess && showEdit && (
            <EditIcon
              sx={{
                color: '#2F736E',
                fontSize: '1.25rem',
                cursor: 'pointer',
              }}
              onClick={onEdit}
            />
          )}
        </Stack>
        {renderButton && renderButton()}
        {!isNullOrUndefined(statusIndicator) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '14px',
                lineHeight: '16px',
                color: '#162936',
              }}
            >
              {statusIndicator.text}
            </Typography>
            <Box
              sx={{
                width: '12px',
                height: '12px',
                borderRadius: '2px',
                backgroundColor: statusIndicator.color,
              }}
            />
          </Box>
        )}
        {!hideExpand && (
          <ExpandMoreIcon
            onClick={() => !disableExpand && handleToggleAccordion()}
            sx={{
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease-in-out',
              cursor: disableExpand ? 'not-allowed' : 'pointer',
            }}
            aria-expanded={isExpanded}
          />
        )}
      </Stack>

      <Collapse
        in={isExpanded}
        timeout={200}
        unmountOnExit
        sx={{
          transition: 'height 0.3s ease-in-out, padding 0.3s ease-in-out',
        }}
      >
        <Box
          sx={{
            overflow: 'auto',
            paddingTop: isExpanded ? '0.75rem' : '0',
            transition: 'padding 0.3s ease-in-out',
          }}
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

export default CustomAccordion;
